import Sortable from 'sortablejs';
import ApplicationController from './application_controller';

// Form Designer canvas — the custom-form editor's main tab, where a form's structure and layout
// are designed together. Every control becomes a Bootstrap `.col-md-{w}` cell inside one
// auto-wrapping `.row`; the cell body hosts the control's live editor render (the very node the
// legacy structure editor used to append to its own canvas), so the grid is what-you-see-is-
// what-you-get. Cells reorder by dragging the toolbar strip (SortableJS) and resize by the
// corner grip, clamped to the slug's declared minimum.
//
// Division of labor with the jQuery editor singleton (custom_forms/_script.html.erb, exposed as
// window.customFormEditor): the editor owns the controls array, the per-control settings modals,
// the palette and the save path; this controller owns the grid — order, widths and the cell
// chrome. They talk through document events:
//
//   editor → designer   custom-form-editor:ready / :sync-layout / :controls-changed
//   designer → editor   custom-form-editor:layout-reordered   (after a drag)
//                       custom-form-designer:control-action   (gear / clone / remove / undo / redo, and
//                                                              add: a palette button dropped at an index)
//                       custom-form-designer:before-change    (a drag, resize or reset is about to
//                                                              change the layout: take an undo snapshot)
//                       custom-form-designer:rendered         (cells rebuilt; repaint decorations)
//
// Ordering: the editor's controls array is the single source of truth. The saved layout only
// contributes widths; render() always paints in controls order and a drag here pushes the new
// order back through `layout-reordered`. On every change we re-serialize to the hidden textarea,
// walking the cells in DOM order and computing `x`/`y` from width accumulation, so the saved
// `{grid:{cells:[...]}}` schema is unchanged from the Layout designer this replaces.
export default class extends ApplicationController {
  static targets = ['canvas', 'hidden', 'emptyNotice', 'palette'];

  // Sortable group names: cells sort among themselves, and accept drops cloned out of the
  // palette lists (which never accept anything and never reorder).
  static CELLS_GROUP = 'form-designer-cells';

  static PALETTE_GROUP = 'form-designer-palette';

  static values = {
    layout: Object,
    minWidths: Object,
    defaultMinWidth: Number,
    slugNames: Object,
  };

  connect() {
    this.boundOnEditorReady = this.onEditorReady.bind(this);
    this.boundOnSyncLayout = this.onSyncLayout.bind(this);
    this.boundOnControlsChanged = this.onControlsChanged.bind(this);
    document.addEventListener('custom-form-editor:ready', this.boundOnEditorReady);
    document.addEventListener('custom-form-editor:sync-layout', this.boundOnSyncLayout);
    document.addEventListener('custom-form-editor:controls-changed', this.boundOnControlsChanged);
    this.seedHiddenFromValue();
    this.render();
    this.initPaletteSortables();
  }

  disconnect() {
    this.destroySortable();
    (this.paletteSortables || []).forEach((sortable) => sortable.destroy());
    this.paletteSortables = [];
    document.removeEventListener('custom-form-editor:ready', this.boundOnEditorReady);
    document.removeEventListener('custom-form-editor:sync-layout', this.boundOnSyncLayout);
    document.removeEventListener('custom-form-editor:controls-changed', this.boundOnControlsChanged);
  }

  onEditorReady() {
    this.render();
  }

  // Reconcile the layout against the current controls array on demand. Dispatched by the editor's
  // save and preview paths so every control is in the saved `grid.cells` before the hidden
  // textarea is read. render() is synchronous and serialize() writes the textarea before this
  // returns, so the caller can read it immediately after dispatching.
  onSyncLayout() {
    this.render();
  }

  // Editor mutations (add, clone, delete, move, label or meaning edit) dispatch `controls-changed`
  // so the canvas repaints right away. Coalesced through a microtask: the template-load path
  // fires one dispatch per control in a single synchronous loop, and this collapses the burst
  // into one render. The save path must NOT use this — it relies on the synchronous handler above.
  onControlsChanged() {
    if (this.renderQueued) return;
    this.renderQueued = true;
    Promise.resolve().then(() => {
      this.renderQueued = false;
      if (!this.element.isConnected) return;
      this.render();
    });
  }

  // "Reset to stacked layout": forget every saved width so the next render seeds each control
  // full-width, in controls order. Nothing to do (or undo) when everything is already stacked.
  reset() {
    const cols = Array.from(this.canvasTarget.querySelectorAll('.layout-cell'));
    if (cols.every((col) => Number(col.dataset.w) === 12)) return;
    this.announceChange();
    this.hiddenTarget.value = '{}';
    this.render();
  }

  // The editor keeps the undo history for this tab (it owns the controls); it snapshots the
  // current state when it hears this, so it must fire BEFORE the change lands.
  announceChange() {
    document.dispatchEvent(new CustomEvent('custom-form-designer:before-change'));
  }

  destroySortable() {
    if (this.sortable) {
      this.sortable.destroy();
      this.sortable = null;
    }
  }

  // The palette lists are drag sources: a button dragged out is cloned (the palette keeps its
  // button), and while it hovers over the canvas it shows as the box it will become (see the
  // .layout-cell-drop-preview styles). Disabled and incompatible buttons stay put.
  initPaletteSortables() {
    this.paletteSortables = this.paletteTargets.map(
      (list) =>
        new Sortable(list, {
          group: { name: this.constructor.PALETTE_GROUP, pull: 'clone', put: false },
          sort: false,
          draggable: '.btn-form-editor',
          filter: '.disabled, [disabled], .incompatible',
          preventOnFilter: false,
          ghostClass: 'layout-cell-drop-preview',
          animation: 150,
        })
    );
  }

  // A palette button dropped onto the canvas: SortableJS has put the button itself where it was
  // dropped (its clone stays in the palette). Take the button back out and ask the editor to
  // add that component at the drop position — the editor builds it, opens its settings modal
  // and records the undo step exactly as a palette click does.
  onCanvasAdd(event) {
    const item = event && event.item;
    if (!item || !item.matches('.btn-form-editor')) return;
    const { slug } = item.dataset;
    const index = Number.isInteger(event.newIndex) ? event.newIndex : this.currentControls().length;
    item.remove();
    if (!slug) return;
    document.dispatchEvent(
      new CustomEvent('custom-form-designer:control-action', { detail: { action: 'add', slug, index } })
    );
  }

  toggleEmptyNotice(empty) {
    if (!this.hasEmptyNoticeTarget) return;
    this.emptyNoticeTarget.classList.toggle('hidden', !empty);
  }

  seedHiddenFromValue() {
    const current = this.hiddenTarget.value.trim();
    if (current && current !== '{}') return;
    this.hiddenTarget.value = JSON.stringify(this.layoutValue || {});
  }

  currentControls() {
    const raw = (window.customFormEditor && window.customFormEditor.controls) || [];
    return (Array.isArray(raw) ? raw : Object.values(raw)).filter((c) => c && c.id);
  }

  minWidthFor(control) {
    const map = this.minWidthsValue || {};
    return map[control.slug] || this.defaultMinWidthValue || 3;
  }

  // The type name shown on the toolbar. Prefer the control's settings-modal title: it follows
  // the template's form type ("Session Start Time" on a visit note, "Document Start Date" on a
  // plan of care) exactly as the legacy structure editor labelled its rows. Falls back to the
  // component catalog name, then the slug.
  typeName(control) {
    const modal = elementOf(control.$modal);
    const title = modal && modal.querySelector('.modal-title');
    const text = title ? title.textContent.trim() : '';
    return text || (this.slugNamesValue && this.slugNamesValue[control.slug]) || control.slug;
  }

  readHidden() {
    try {
      const v = this.hiddenTarget.value.trim();
      return v ? JSON.parse(v) : {};
    } catch {
      return {};
    }
  }

  // Reconcile the saved layout with the current controls array, then paint the canvas. Saved
  // cells only contribute their widths, keyed by control_id; controls absent from the saved
  // layout auto-seed full-width at their position, and saved cells referencing controls that no
  // longer exist fall away. Cells are rebuilt from scratch on every render — cheap, and it keeps
  // the toolbar chrome in step with the control — while each control's rendered node is moved
  // (never cloned) into its new cell, so the editor's per-control update code keeps working on
  // the same element it has always held.
  render() {
    const controlsList = this.currentControls();
    this.toggleEmptyNotice(controlsList.length === 0);

    // No controls means either the editor hasn't instantiated the template's controls yet (on a
    // Turbo visit this controller connects a beat before the editor's ready handler runs) or
    // the last one was just deleted. Either way, leave the hidden textarea alone: it carries the
    // SAVED layout, and serializing an empty grid here would wipe every saved width before the
    // controls arrive to claim them. A stale cell for a deleted control simply falls away on
    // the next render, and nothing can be saved with an empty form anyway.
    if (controlsList.length === 0) {
      this.destroySortable();
      this.canvasTarget.querySelectorAll(':scope > .layout-cell').forEach((el) => el.remove());
      document.dispatchEvent(new CustomEvent('custom-form-designer:rendered'));
      return;
    }

    const incoming = this.readHidden();
    const rawCells = toCellArray(incoming && incoming.grid && incoming.grid.cells);
    const cellsByControlId = {};
    rawCells.forEach((cell) => {
      if (cell && cell.control_id && !cellsByControlId[cell.control_id]) cellsByControlId[cell.control_id] = cell;
    });

    this.destroySortable();
    const canvas = this.canvasTarget;
    // Native removal on purpose: jQuery's .empty()/.html() would also purge the event handlers
    // and data the editor bound on the control nodes we are about to re-home.
    canvas.querySelectorAll(':scope > .layout-cell').forEach((el) => el.remove());
    canvas.classList.add('row', 'layout-canvas-row');

    controlsList.forEach((control) => {
      const cell = cellsByControlId[control.id] || { w: 12 };
      const minW = this.minWidthFor(control);
      const w = clamp(Number(cell.w) || 12, minW, 12);
      canvas.appendChild(this.buildCell(control, w, minW));
    });

    this.sortable = new Sortable(canvas, {
      animation: 150,
      group: { name: this.constructor.CELLS_GROUP, put: [this.constructor.PALETTE_GROUP] },
      // The whole toolbar strip drags; the buttons and links inside it are filtered out of drag
      // starts but keep working as clicks.
      handle: '.layout-cell-toolbar',
      filter: '.layout-cell-action, a',
      preventOnFilter: false,
      draggable: '.layout-cell',
      ghostClass: 'layout-cell-ghost',
      onAdd: (event) => this.onCanvasAdd(event),
      onEnd: () => {
        // A drag dropped back where it started changes nothing — no snapshot, no reorder.
        const ids = Array.from(canvas.querySelectorAll('.layout-cell')).map((col) => col.dataset.controlId);
        const current = this.currentControls().map((control) => control.id);
        if (ids.join(',') === current.join(',')) return;
        this.announceChange();
        this.serialize();
        this.notifyReorder();
      },
    });

    this.serialize();
    document.dispatchEvent(new CustomEvent('custom-form-designer:rendered'));
  }

  // After a drag, hand the new order back to the editor so its controls array (the single source
  // of truth for order) matches the canvas. The editor listens for this and re-sequences the
  // array; the cells are already in the right place, so no re-render follows.
  notifyReorder() {
    const ids = Array.from(this.canvasTarget.querySelectorAll('.layout-cell')).map((col) => col.dataset.controlId);
    document.dispatchEvent(new CustomEvent('custom-form-editor:layout-reordered', { detail: { ids } }));
  }

  buildCell(control, w, minW) {
    const col = document.createElement('div');
    col.className = `col-12 col-md-${w} layout-cell`;
    col.dataset.controlId = control.id;
    col.dataset.slug = control.slug;
    col.dataset.minW = String(minW);
    col.dataset.w = String(w);

    // Category tint: blue for visual, green for smart, grey for standard — and "semi-smart"
    // pastel blue for a standard control that carries a Field meaning (semantic_role): more than
    // plain standard, less than a true smart control.
    const role =
      (control.properties && control.properties.semantic_role && control.properties.semantic_role.value) || '';
    const semiSmartClass = control.category === 'standard' && role ? ' layout-cell-semi-smart' : '';
    // layout-cell-control marks a cell hosting a live control (overflow stays visible so the
    // control's own dropdown menus can escape the cell).
    const categoryClass = ` layout-cell-control${control.category ? ` layout-cell-${control.category}` : ''}${semiSmartClass}`;
    const defaultMin = this.defaultMinWidthValue || 3;
    const minHint =
      minW > defaultMin
        ? `<span class="layout-width-min" title="${escapeHtml(control.slug)} cannot shrink below ${minW}/12">min ${minW}</span>`
        : '';
    // Smart controls are singletons (the editor refuses a second copy of the same slug), so a
    // clone button on them could only ever produce the "only one of these" error.
    const cloneButton =
      control.category === 'smart'
        ? ''
        : '<button type="button" class="btn layout-cell-action layout-cell-clone" data-action="form-designer#clone" title="Clone this component (adds a copy at the end of the form)"><i class="bi bi-c-square"></i></button>';
    col.innerHTML = `
      <div class="layout-cell-inner${categoryClass}">
        <div class="layout-cell-toolbar" title="Drag to reorder">
          <span class="layout-cell-tools">
            <i class="bi bi-grip-vertical layout-drag-handle"></i>
            <button type="button" class="btn layout-cell-action layout-cell-settings" data-action="form-designer#edit" title="Component settings"><i class="bi bi-gear"></i></button>
            ${cloneButton}
          </span>
          <span class="layout-cell-slug" title="${escapeHtml(control.slug)}">${escapeHtml(this.typeName(control))}</span>
          <em class="control-semantic-role text-muted small"></em>
          ${minHint}
          <span class="layout-width-value" title="Width (drag the corner grip to resize)">${w}/12</span>
          <button type="button" class="layout-cell-action layout-cell-remove" data-action="form-designer#remove" title="Remove this component from the form"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="layout-cell-body"></div>
        <div class="layout-cell-resize-grip" data-action="mousedown->form-designer#startResize" title="Drag to resize"></div>
      </div>
    `;

    // Host the control's live render. A control without one (the editor hasn't built it yet)
    // shows its label so the cell is never blank.
    const body = col.querySelector('.layout-cell-body');
    const node = elementOf(control.$html);
    if (node) {
      body.appendChild(node);
    } else {
      const label =
        (control.properties && control.properties.label_text && control.properties.label_text.value) || control.slug;
      const text = document.createElement('div');
      text.className = 'layout-cell-label-text';
      text.textContent = label;
      body.appendChild(text);
    }
    return col;
  }

  // --- Toolbar actions: the editor owns the behavior (modals, confirmation, the controls
  // array), so each button just names the control and what was asked of it. ---

  edit(event) {
    this.dispatchControlAction('edit', event);
  }

  clone(event) {
    this.dispatchControlAction('clone', event);
  }

  remove(event) {
    this.dispatchControlAction('delete', event);
  }

  // The Undo / Redo buttons in the card header; the editor owns the history.
  undo() {
    document.dispatchEvent(new CustomEvent('custom-form-designer:control-action', { detail: { action: 'undo' } }));
  }

  redo() {
    document.dispatchEvent(new CustomEvent('custom-form-designer:control-action', { detail: { action: 'redo' } }));
  }

  dispatchControlAction(action, event, extra = {}) {
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    document.dispatchEvent(
      new CustomEvent('custom-form-designer:control-action', {
        detail: { action, id: col.dataset.controlId, ...extra },
      })
    );
  }

  // Drag-to-resize from the bottom-right corner grip. Shows a faded-yellow ghost tracking the
  // target width as the user drags, and only commits the new width (with serialize + layout
  // reflow) on mouseup. That way the row doesn't jitter through intermediate sizes as the user
  // sweeps across. Vertical drag is ignored — cell height is intrinsic to the control.
  startResize(event) {
    event.preventDefault();
    const col = event.currentTarget.closest('.layout-cell');
    if (!col) return;
    const rowEl = col.parentElement;
    if (!rowEl) return;
    const unitWidth = rowEl.getBoundingClientRect().width / 12;
    if (!unitWidth || !Number.isFinite(unitWidth)) return;
    const startX = event.clientX;
    const startW = Number(col.dataset.w) || 12;
    const minW = Number(col.dataset.minW) || 3;
    const colRect = col.getBoundingClientRect();

    const ghost = document.createElement('div');
    ghost.className = 'layout-cell-resize-ghost';
    ghost.style.top = `${colRect.top}px`;
    ghost.style.left = `${colRect.left}px`;
    ghost.style.height = `${colRect.height}px`;
    ghost.style.width = `${colRect.width}px`;
    const label = document.createElement('span');
    label.className = 'layout-cell-resize-ghost-label';
    label.textContent = `${startW} / 12`;
    ghost.appendChild(label);
    document.body.appendChild(ghost);

    let targetW = startW;

    const onMove = (e) => {
      const dw = Math.round((e.clientX - startX) / unitWidth);
      const nextW = clamp(startW + dw, minW, 12);
      targetW = nextW;
      ghost.style.width = `${nextW * unitWidth}px`;
      label.textContent = `${nextW} / 12`;
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('layout-cell-resizing');
      ghost.remove();
      if (targetW !== startW) {
        this.setCellWidth(col, targetW);
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.classList.add('layout-cell-resizing');
  }

  setCellWidth(col, nextW) {
    const minW = Number(col.dataset.minW) || 3;
    const w = clamp(nextW, minW, 12);
    if (w === Number(col.dataset.w)) return;
    this.announceChange();
    // Swap the col-md-{n} class
    Array.from(col.classList).forEach((cls) => {
      if (/^col-md-\d+$/.test(cls)) col.classList.remove(cls);
    });
    col.classList.add(`col-md-${w}`);
    col.dataset.w = String(w);
    const valueEl = col.querySelector('.layout-width-value');
    if (valueEl) valueEl.textContent = `${w}/12`;
    this.serialize();
  }

  // Walk the DOM in order, computing x/y from width accumulation: when adding a cell would
  // exceed 12 columns in the current row, wrap to the next row. This matches Bootstrap's
  // flex-wrap visual behavior, so the saved schema reflects what the designer sees.
  serialize() {
    const cells = [];
    let x = 0;
    let y = 0;
    this.canvasTarget.querySelectorAll('.layout-cell').forEach((col) => {
      const w = Number(col.dataset.w) || 12;
      if (x + w > 12) {
        y += 1;
        x = 0;
      }
      cells.push({ control_id: col.dataset.controlId, x, y, w });
      x += w;
      if (x >= 12) {
        y += 1;
        x = 0;
      }
    });
    this.hiddenTarget.value = JSON.stringify({ grid: { columns: 12, cells } });
  }
}

// The editor stores its DOM handles as jQuery objects ($html, $modal); accept those, bare
// elements, or nothing.
function elementOf(handle) {
  if (!handle) return null;
  if (handle instanceof Element) return handle;
  return handle[0] instanceof Element ? handle[0] : null;
}

function toCellArray(cells) {
  if (Array.isArray(cells)) return cells;
  if (cells && typeof cells === 'object') return Object.values(cells);
  return [];
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
